import type { QueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { scrubReportingPrivateCache } from 'src/queries/reporting/reporting.cache'

import type { ReportingAccessDeniedEvent } from './reportingConversation.types'

interface ReportingConversationAccessOptions {
  actorId?: string
  organizationId?: string
  isAuthenticated: boolean
  queryClient: QueryClient
  resetPrivateState: () => void
  onAccessDenied: (event: ReportingAccessDeniedEvent) => void
}

/** Owns authorization-scope transitions and private Reporting cache cleanup. */
export const useReportingConversationAccess = ({
  actorId,
  organizationId,
  isAuthenticated,
  queryClient,
  resetPrivateState,
  onAccessDenied,
}: ReportingConversationAccessOptions) => {
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<
    number | undefined
  >()
  const accessDeniedRef = useRef(false)
  const scopeRef = useRef({ actorId, organizationId, isAuthenticated })
  const onAccessDeniedRef = useRef(onAccessDenied)

  useEffect(() => {
    onAccessDeniedRef.current = onAccessDenied
  }, [onAccessDenied])

  const denyAccess = useCallback(
    (status = 403) => {
      if (accessDeniedRef.current) return
      accessDeniedRef.current = true
      resetPrivateState()
      scrubReportingPrivateCache(queryClient, actorId, organizationId)
      setAccessDeniedStatus(status)
      onAccessDeniedRef.current({ reason: 'transport', status })
    },
    [actorId, organizationId, queryClient, resetPrivateState],
  )

  useEffect(() => {
    const previous = scopeRef.current
    const scopeChanged =
      previous.actorId !== actorId || previous.organizationId !== organizationId
    if (isAuthenticated && !scopeChanged) {
      accessDeniedRef.current = false
      setAccessDeniedStatus(undefined)
      scopeRef.current = { actorId, organizationId, isAuthenticated }
      return
    }
    resetPrivateState()
    scrubReportingPrivateCache(
      queryClient,
      previous.actorId,
      previous.organizationId,
    )
    if (!accessDeniedRef.current) {
      accessDeniedRef.current = true
      setAccessDeniedStatus(403)
      onAccessDeniedRef.current({ reason: 'scope_change' })
    }
    scopeRef.current = { actorId, organizationId, isAuthenticated }
  }, [actorId, isAuthenticated, organizationId, queryClient, resetPrivateState])

  return { accessDeniedStatus, accessDeniedRef, denyAccess }
}
